import {Button, Typography, useMediaQuery, Grid} from '@material-ui/core';
import {Backup} from '@material-ui/icons';
import {HealthPlanDto, LifePlanDto} from 'Agent/Policies/api';
import PolicyImagesCard from 'Agent/Policies/components/PolicyImagesCard';
import {clearPolicy, fetchPolicy, fetchPolicyImages} from 'Agent/Policies/ducks/policy';
import {PolicySource} from 'Agent/Policies/models/PolicySource';
import PolicyRouteConstants from 'Agent/Policies/PolicyRouteConstants';
import classnames from 'classnames';
import {useBreadcrumbs} from 'Core/hooks/useBreadcrumbs';
import {matchAsync} from 'Core/models/Async';
import React, {useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {RouteComponentProps, withRouter} from 'react-router';
import {ReducerState} from 'rootReducer';
import AllstateCard from 'Shared/components/AllstateCard';
import CardField from 'Shared/components/CardField';
import ErrorPage from 'Shared/components/ErrorPage';
import Loading from 'Shared/components/Loading';
import RequireScope from 'Shared/components/RequireScope';
import withSelectedAgency, {WithSelectedAgencyProps} from 'Shared/components/withSelectedAgency';
import {Scope} from 'Shared/constants/Scope';
import {toFormattedDateAsUTC} from 'Shared/utilities/DateFormatter';
import {toMoneyValue} from 'Shared/utilities/FormatMoney';
import {maskPhoneNumber} from 'Shared/utilities/phoneNumberMasker';
import {selectUserScopes} from 'Users/selectors/userProfileSelectors';
import {ModalState, showModal} from 'Core/ducks/modal';
import './PolicyDetails.sass';

type PolicyStatusProps = RouteComponentProps<{policyId: string; policyNumber: string}> & WithSelectedAgencyProps;

type LifePolicyCardProps = {
  lifePolicy: LifePlanDto;
};

const LifePolicyCard = (props: LifePolicyCardProps) => {
  return (
    <AllstateCard title="Plan Information" padding>
      <CardField name="Plan" value={props.lifePolicy.planCode} />
      <CardField name="Description" value={props.lifePolicy.planDescription} />
      <CardField name="Coverage Type" value={props.lifePolicy.coverageType} />
      <CardField name="Effective Date" value={toFormattedDateAsUTC(props.lifePolicy.effectiveDate)} />
      <CardField name="Insurance Amount" value={toMoneyValue(props.lifePolicy.amtOfInsurance)} />
      <CardField name="Issue Age" value={props.lifePolicy.issueAge} />
      <CardField name="Death Benefit Option" value={props.lifePolicy.deathBenefitOption} />
      <CardField name="Commission Phase" value={props.lifePolicy.commissionPhase} />
    </AllstateCard>
  );
};

type HealthPolicyCardProps = {
  healthPolicy: HealthPlanDto;
  sourceName: string;
};

const HealthPolicyCard = (props: HealthPolicyCardProps) => (
  <AllstateCard title="Plan Information" padding>
    <CardField name="Plan" value={props.healthPolicy.commonPlan} />
    <CardField name="Plan Code" value={props.healthPolicy.planCode} />
    <CardField name="Description" value={props.healthPolicy.planDescription} />
    <CardField name="Coverage Type" value={props.healthPolicy.coverageType} />
    <CardField name="Effective Date" value={toFormattedDateAsUTC(props.healthPolicy.effectiveDate)} />
    {props.sourceName === 'GROUP' || props.sourceName === 'LIFE' ? (
      <CardField name="Termination Date" value={toFormattedDateAsUTC(props.healthPolicy.terminationDate)} />
    ) : (
      <CardField name="Issue Age" value={props.healthPolicy.issueAge} />
    )}
    <CardField name="Units" value={props.healthPolicy.units} />
    <CardField name="Elimination Period Accident" value={props.healthPolicy.eliminationPeriodAccident} />
    <CardField name="Elimination Period Sickness" value={props.healthPolicy.eliminationPeriodSickness} />
    <CardField name="Benefit Period Accident" value={props.healthPolicy.benefitPeriodAccident} />
    <CardField name="Benefit Period Sickness" value={props.healthPolicy.benefitPeriodSickness} />
    <CardField name="Commission Phase" value={props.healthPolicy.commissionPhase} />
  </AllstateCard>
);

export const PolicyDetails = (props: PolicyStatusProps) => {
  const isDesktop = useMediaQuery((theme: any) => theme.breakpoints.up('lg'));
  const dispatch = useDispatch();
  const scopes = useSelector(selectUserScopes);
  const {policy} = useSelector((state: ReducerState) => state.policy);
  const {match} = props;
  const {policyId, policyNumber} = match.params;
  useBreadcrumbs(policyNumber, props.match.url);
  const {selectedAgency} = props;

  useEffect(() => {
    dispatch(fetchPolicy(selectedAgency.encryptedIdentifier, policyId, policyNumber));
    dispatch(fetchPolicyImages(selectedAgency.encryptedIdentifier, policyId));

    return () => {
      dispatch(clearPolicy());
    };
  }, [policyId, policyNumber]);

  return matchAsync(policy, {
    Conflict: error => <ErrorPage error={error} />,
    Loaded: policy => (
      <div
        className={classnames('policy-details', {
          'policy-details--desktop': isDesktop,
        })}
      >
        <Grid container justify="space-between" className="policy-details__header">
          <Typography className="policy-details__title" variant="h6">
            Policy {policy.policyNumber}
          </Typography>
          <Grid item>
            {policy.source!.id !== PolicySource.Group && (
              <RequireScope scopes={scopes} scope={Scope.UploadPolicyDocument}>
                <Button
                  className="policy-details__button"
                  variant="contained"
                  color="primary"
                  startIcon={<Backup />}
                  onClick={() =>
                    props.history.push(PolicyRouteConstants.POLICY_UPLOAD_DOCUMENT(policy.id!, policy.policyNumber!))
                  }
                >
                  Upload Document
                </Button>
              </RequireScope>
            )}
            <RequireScope scopes={scopes} scope={Scope.SearchClaims}>
              <Button
                className="policy-details__button"
                variant="contained"
                color="primary"
                onClick={() => dispatch(showModal(ModalState.ClaimsConfirmation({policyNumber: policy.policyNumber!})))}
              >
                View Claims
              </Button>
            </RequireScope>
            <RequireScope scopes={scopes} scope={Scope.ViewCorrespondenceDetails}>
              <Button
                className="policy-details__button"
                variant="contained"
                color="primary"
                onClick={() =>
                  props.history.push(PolicyRouteConstants.CORRESPONDENCE_SUMMARIES(undefined, policy.policyNumber!))
                }
              >
                View Letters
              </Button>
            </RequireScope>
          </Grid>
        </Grid>
        <div className="policy-details__status-column">
          <AllstateCard title="Policy Details" padding>
            <CardField name="Status" value={policy.status} />
            <CardField name="Number" value={policy.policyNumber} />
            <CardField name="Issue Date" value={toFormattedDateAsUTC(policy.issueDate)} />
            <CardField name="Special Handling" value={policy.specialHandlingInstruction} />
          </AllstateCard>
          <AllstateCard title="Account" padding>
            <CardField name="Name" value={policy.accountName} />
            <CardField name="Number" value={policy.accountNumber} />
            <CardField name="Previous Name" value={policy.previousAccountName} />
            <CardField name="Previous Number" value={policy.previousAccountNumber} />
          </AllstateCard>
          {policy.agentOfRecord && (
            <AllstateCard title="Agent Of Record" padding>
              <CardField name="Name" value={policy.agentOfRecord.name} />
              <CardField name="Number" value={policy.agentOfRecord.number} />
            </AllstateCard>
          )}
          {policy.servicingAgent && (
            <AllstateCard title="Servicing Agent" padding>
              <CardField name="Name" value={policy.servicingAgent.name} />
              <CardField name="Number" value={policy.servicingAgent.number} />
            </AllstateCard>
          )}
        </div>
        <div className="policy-details__policy-information-container">
          <AllstateCard title="Insured Information" padding>
            <CardField name="Name" value={policy.insured && policy.insured.fullName} />
            <CardField
              name="Date of Birth"
              value={toFormattedDateAsUTC(policy.insured && policy.insured.dateOfBirth)}
            />
            <CardField name="SSN" value={policy.insured?.identifier && `***-**-${policy.insured.identifier}`} />
            <CardField
              name="Address"
              value={policy.insured && policy.insured.address && policy.insured.address!.fullAddress}
            />
          </AllstateCard>
          <AllstateCard title="Owner Information" padding>
            <CardField name="Name" value={policy.owner && policy.owner.fullName} />
            <CardField name="Date of Birth" value={toFormattedDateAsUTC(policy.owner && policy.owner.dateOfBirth)} />
            <CardField name="SSN" value={policy.owner?.identifier && `***-**-${policy.owner.identifier}`} />
            <CardField name="Phone" value={maskPhoneNumber(policy.owner?.phoneNumber)} />
            <CardField
              name="Address"
              value={policy.owner && policy.owner.address && policy.owner.address!.fullAddress}
            />
          </AllstateCard>
          <RequireScope scopes={scopes} scope={Scope.ViewPolicyImges}>
            <PolicyImagesCard selectedAgency={selectedAgency} />
          </RequireScope>
          {policy.lifePlans &&
            policy.lifePlans.map((lifePlan, index) => (
              <LifePolicyCard key={`life-policy_${index}`} lifePolicy={lifePlan} />
            ))}
          {policy.healthPlans &&
            policy.healthPlans.map((healthPlan, index) => (
              <HealthPolicyCard
                sourceName={policy.source!.name!}
                key={`health-policy_${index}`}
                healthPolicy={healthPlan}
              />
            ))}
          <AllstateCard title="Premium Payment Summary" padding>
            <CardField name="Status" value={policy.status} />
            <CardField name="Mode Premium" value={toMoneyValue(policy.premium)} />
            <CardField name="Bill Premium" value={toMoneyValue(policy.billingPremium)} />
            <CardField name="Last Payment" value={toMoneyValue(policy.lastPayAmount)} />
            <CardField name="Payment Date" value={toFormattedDateAsUTC(policy.lastPayDate)} />
          </AllstateCard>
          {policy.commissionAgents &&
            policy.commissionAgents.map((commissionAgent, index) => (
              <AllstateCard key={`commission-agent_${index}`} title={'Commission Agent'} padding>
                <CardField name="Name" value={commissionAgent.name} />
                <CardField name="Number" value={commissionAgent.number} />
                <CardField name="Commission Phase" value={commissionAgent.commissionPhase} />
                <CardField name="Commission Split" value={commissionAgent.commissionPercent} />
              </AllstateCard>
            ))}
        </div>
      </div>
    ),
    Loading: () => <Loading />,
    ServerError: error => <ErrorPage error={error} />,
    Unauthorized: error => <ErrorPage error={error} />,
  });
};

export default withSelectedAgency(withRouter(PolicyDetails));
